import { useQuery } from '@apollo/client';
import { RoleAccess } from 'app-types/core';

import { MyAccessQuery } from '../../../gql/core/queries';

/**
 * Effective access for the current user (cached app-wide by Apollo — calling
 * this in many components triggers one fetch). Use `canAccessModule` /
 * `canAccessSection` to gate nav tabs, settings sections and routes.
 *
 * FAIL-OPEN: while loading, or if the backend doesn't return `access`
 * (`appModules`/`settingsSections` are `undefined`), nothing is hidden. Only a
 * genuinely returned empty array hides everything for that area. This prevents
 * the nav/sidebar from collapsing when access data isn't available yet.
 */
export function useMyAccess() {
  const { data, loading } = useQuery<{
    user: { isCustomRole?: boolean; access?: RoleAccess };
  }>(MyAccessQuery, {
    context: { clientName: 'core' },
    // A backend without the `access` field must not throw — fail open instead.
    errorPolicy: 'all',
  });

  const access = data?.user?.access;
  const appModules = access?.appModules;
  const settingsSections = access?.settingsSections;

  return {
    appModules,
    settingsSections,
    // True only for users governed by a custom role. Such users carry no system
    // staff role, so route guards must gate them by access (module/section)
    // alone, not by the legacy system-role list.
    isCustomRole: !!data?.user?.isCustomRole,
    canAccessModule: (key: string) => !appModules || appModules.includes(key),
    canAccessSection: (key: string) => !settingsSections || settingsSections.includes(key),
    loading,
  };
}
